import type { QueryClient } from "@tanstack/react-query";

import {
  getDealOfferSummaryQueryOptions,
  getDealOffersQueryOptions,
} from "@/components/marketplace/deal-offers-query";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { getReleaseAlternativePackingQueryOptions } from "@/components/marketplace/release-alternative-packing-query";
import { selectReleaseOffers } from "@/components/marketplace/selectReleaseOffers";
import { buildReleaseSeoOffer, type ReleaseSeoOffer } from "@/lib/build-release-seo-offer";

interface PrefetchReleaseSeoOfferInput {
  queryClient: QueryClient;
  releaseId: string;
  workId: string | null;
  currency: string;
  format: "vinyl" | "cd" | "cassette" | "video" | "other" | "all";
  viewerId: string | null;
}

/** Prefetches the release commerce query and returns its serializable SEO projection. */
export async function prefetchReleaseSeoOffer(
  input: PrefetchReleaseSeoOfferInput,
): Promise<ReleaseSeoOffer | null> {
  if (input.workId) {
    const summaryQueryOptions = getDealOfferSummaryQueryOptions(
      {
        workId: input.workId,
        releaseId: input.releaseId,
        currency: input.currency,
        format: input.format,
      },
      { viewerId: input.viewerId },
    );
    await Promise.all([
      input.queryClient.prefetchQuery(summaryQueryOptions),
      input.queryClient.prefetchQuery(
        getDealOffersQueryOptions(
          { workId: input.workId, currency: input.currency },
          { viewerId: input.viewerId },
        ),
      ),
    ]);
    const workOffersQuery = getDealOffersQueryOptions(
      { workId: input.workId, currency: input.currency },
      { viewerId: input.viewerId },
    );
    const workOffers = input.queryClient.getQueryData<{ offers: DealOffer[] }>(
      workOffersQuery.queryKey,
    );
    const { alternatives } = selectReleaseOffers(workOffers?.offers ?? [], input.releaseId);
    if (alternatives.length > 0) {
      await input.queryClient.prefetchQuery(
        getReleaseAlternativePackingQueryOptions({
          workId: input.workId,
          releaseIds: alternatives.map((offer) => offer.matchedReleaseIds[0]!),
        }),
      );
    }
  }

  const queryOptions = getDealOffersQueryOptions(
    {
      releaseId: input.releaseId,
      currency: input.currency,
    },
    { viewerId: input.viewerId },
  );
  await input.queryClient.prefetchQuery(queryOptions);
  const result = input.queryClient.getQueryData<{ offers: DealOffer[] }>(queryOptions.queryKey);
  return buildReleaseSeoOffer({ offers: result?.offers });
}
